import { useEffect, useMemo, useState } from 'react';
import {
  getSurveySampleByIdApi,
  getSurveySampleDimensionsByIdApi,
} from '../../../api/survey.sample';
import { QUESTION_FILTER_TYPE, QUESTION_VALUE_TYPE } from '../../../constants/common';
import { getFilterSampleId, removeHiddenAttributeOptions } from '../../../helpers/filters';

const NO_HIDDEN = { ids: [], labels: [] };

// Multi-select samples hide tags by dimension id; resolve those to the
// dimension labels as well, since the filter options may only carry labels.
const loadHidden = async (sampleId, jwt) => {
  const sample = await getSurveySampleByIdApi(sampleId, jwt);
  const ids = (sample?.Data?.hiddenAnswers || []).map(String);
  if (ids.length === 0) return NO_HIDDEN;
  const labelMode = [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(
    sample.Data.valueType
  );
  if (!labelMode) return { ids, labels: [] };
  const dimensions = (await getSurveySampleDimensionsByIdApi(sampleId, jwt)) || [];
  const labels = dimensions
    .filter((dimension) => ids.includes(String(dimension.Id)))
    .map((dimension) => dimension.Data.label);
  return { ids, labels };
};

// The filters endpoint does not apply the tags hidden in the survey wizard, so
// read each attribute filter's sample and strip its hidden options before the
// filter and segment pickers see them.
const useVisibleFilters = (filters, jwt) => {
  const [hiddenBySampleId, setHiddenBySampleId] = useState({});

  useEffect(() => {
    if (!filters || !jwt) return undefined;
    const sampleIds = [
      ...new Set(
        filters
          .filter((filter) => filter.type === QUESTION_FILTER_TYPE.ATTRIBUTE)
          .map(getFilterSampleId)
          .filter((sampleId) => sampleId !== undefined)
      ),
    ];
    if (sampleIds.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      sampleIds.map((sampleId) =>
        loadHidden(sampleId, jwt)
          .then((hidden) => [sampleId, hidden])
          .catch((e) => {
            console.error(e);
            return [sampleId, NO_HIDDEN];
          })
      )
    ).then((entries) => {
      if (!cancelled) setHiddenBySampleId(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [filters, jwt]);

  return useMemo(
    () => removeHiddenAttributeOptions(filters, hiddenBySampleId),
    [filters, hiddenBySampleId]
  );
};

export default useVisibleFilters;
